import { sortedDeepEntries } from "./object";

// FORMAT DATE
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

export function formatDate(
  date: Date,
  locale: Intl.LocalesArgument | null = "en-US",
  formatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error(`Invalid date value: ${date}`);
  }

  const resolvedLocale = (locale ?? "en-US") as Intl.LocalesArgument & string;

  const formatter = getDateFormatter(resolvedLocale, formatOptions);

  return formatter.format(date);
}

function getDateFormatter(
  locale: Intl.LocalesArgument & string,
  formatOptions: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}-${JSON.stringify(sortedDeepEntries(formatOptions as Record<string, unknown>, 1))}`;

  const existing = dateFormatters.get(key);
  if (existing) return existing;

  if (!Intl.DateTimeFormat.supportedLocalesOf(locale).length) {
    throw new Error(`Unsupported locale: ${locale}`);
  }

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);

  dateFormatters.set(key, formatter);
  return formatter;
}

// FORMAT CURRENCY
export function formatCurrency(
  value: number,
  locale: Intl.LocalesArgument | null = "en-US",
  currency: string | null = "USD",
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    throw new Error(`Invalid number value: ${value}`);
  }

  const resolvedLocale = (locale ?? "en-US") as Intl.LocalesArgument & string;
  const resolvedCurrency = currency ?? "USD";

  try {
    const formatter = getCurrencyFormatter(
      resolvedLocale,
      resolvedCurrency,
      minimumFractionDigits,
      maximumFractionDigits,
    );
    return formatter.format(value);
  } catch (error) {
    console.error(error);
    return value.toString();
  }
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function getCurrencyFormatter(
  locale: Intl.LocalesArgument & string,
  currency: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): Intl.NumberFormat {
  const key = `${locale}-${currency}-${minimumFractionDigits}-${maximumFractionDigits}`;

  const existing = currencyFormatters.get(key);
  if (existing) return existing;

  if (!Intl.NumberFormat.supportedLocalesOf(locale).length) {
    throw new Error(`Unsupported locale: ${locale}`);
  }

  if (!Intl.supportedValuesOf("currency").includes(currency)) {
    throw new Error(`Unsupported currency: ${currency}`);
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  currencyFormatters.set(key, formatter);
  return formatter;
}

export function stripNonDigits(value: string): string {
  let result = "";
  for (const char of value) {
    if (char >= "0" && char <= "9") {
      result += char;
    }
  }
  return result;
}

export function isAllAsciiDigits(s: string): boolean {
  return stripNonDigits(s).length === s.length;
}

export function formatUsZipPlus4(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}
